import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { ChatHistoryListPropsExtended } from '@/types/components/converse/history';
import groupChatHistory from '@/utils/converse/groupChatHistory';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import { createTimeoutSignal } from '@/utils/signalConfig';
import { Close, FilterAlt, Search } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  Popover,
  Slider,
  TextField,
} from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQueryParams } from 'raviger';
import { useEffect, useMemo, useState } from 'react';
import ChatHistoryItem from './ChatHistoryItem';

/**
 * ChatHistoryList component
 *
 * Displays a list of chat history items, grouped by categories.
 * Each chat item can be selected, and the component also displays
 * a message if no chat history is available.
 *
 * @param chatHistory - Array of chat history objects to be displayed
 * @param currentChatID - ID of the currently selected chat
 * @param setCurrentChatID - Callback to set the current chat ID on selection
 * @param filterMoreThanTwoMessages - Whether to filter chats with more than 2 messages
 */

const AVAILABLE_SOURCES = [
  { value: 'integrations.whatsapp', label: 'WhatsApp' },
  { value: 'integrations.telegram', label: 'Telegram' },
  { value: 'integrations.twilio.calls', label: 'Twilio' },
  { value: 'slack', label: 'Slack' },
  { value: 'email', label: 'Email' },
];

interface Filters {
  sources: string[];
  warm: boolean | null;
  messageLength: [number, number];
  title: string;
  campaignId: string;
}

const ChatHistoryList: React.FC<ChatHistoryListPropsExtended> = ({
  currentChatID,
  setCurrentChatID,
  filterMoreThanTwoMessages = false,
  onFilterChange,
}): JSX.Element => {
  const { setStoreState, chatHistory, chatChannel } = useAlchemystStoreForAi(
    (store) => ({
      chatHistory: store.chatHistory,
      setStoreState: store.setStoreState,
      chatChannel: store.chatChannel,
    })
  );

  const [query] = useQueryParams();
  const campaignId = query.campaignId as string;

  const [filteredChats, setFilteredChats] = useState(chatHistory);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Initialize filters with campaignId from URL if present
  const [filters, setFilters] = useState<Filters>({
    sources: [],
    warm: null,
    messageLength: [1, 100],
    title: '',
    campaignId: campaignId || '',
  });

  // Update filters when campaignId in URL changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      campaignId: campaignId || '',
    }));
  }, [campaignId]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSourceToggle = (source: string) => {
    setFilters((prev) => ({
      ...prev,
      sources: prev.sources.includes(source)
        ? prev.sources.filter((s) => s !== source)
        : [...prev.sources, source],
    }));
  };

  const handleWarmChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({
      ...prev,
      warm: event.target.checked,
    }));
  };

  const handleMessageLengthChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    setFilters((prev) => ({
      ...prev,
      messageLength: newValue as [number, number],
    }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    setFilters((prev) => ({
      ...prev,
      title: value,
    }));
  };

  const hasActiveFilters = () => {
    return (
      filters.sources.length > 0 ||
      filters.warm !== null ||
      filters.messageLength[0] > 1 ||
      filters.messageLength[1] < 100 ||
      filters.title.trim() !== '' ||
      filters.campaignId.trim() !== ''
    );
  };

  const buildQueryParams = () => {
    const params = new URLSearchParams();

    // Always include the chat channel
    if (chatChannel) {
      params.append('source', chatChannel);
    }

    // Always include campaignId if present in URL
    if (campaignId) {
      params.append('campaignId', campaignId);
    }

    // Add sources individually to match backend array expectation
    if (filters.sources.length > 0) {
      filters.sources.forEach((source) => {
        params.append('sources', source);
      });
    }

    // Convert boolean warm to 1 as expected by backend
    if (filters.warm) {
      params.append('warm', '1');
    }

    // Add message length filters
    if (filters.messageLength[0] > 1) {
      params.append('minLength', filters.messageLength[0].toString());
    }
    if (filters.messageLength[1] < 100) {
      params.append('maxLength', filters.messageLength[1].toString());
    }

    // Add title search if present
    if (filters.title.trim()) {
      params.append('title', filters.title.trim());
    }

    return `?${params.toString()}`;
  };

  useEffect(() => {
    const filterChats = async () => {
      try {
        const queryString = buildQueryParams();
        const url = `/api/v1/chat/history${queryString}`;

        console.log('🔍 Fetching chat history with params:', {
          url,
          filters: {
            sources: filters.sources,
            warm: filters.warm,
            messageLength: filters.messageLength,
            title: filters.title,
            campaignId: filters.campaignId,
          },
          chatChannel,
        });

        const response = await fetchWithRewrites(url, {
          signal: createTimeoutSignal(),
        });

        const data = await response.json();
        console.log('📥 Received chat history response:', {
          chatsCount: data.chats?.length || 0,
        });

        setFilteredChats(data.chats || []);
      } catch (error) {
        console.error('❌ Error fetching filtered chats:', error);
        setFilteredChats([]);
      }
    };

    filterChats();
  }, [filters, chatChannel]);

  const open = Boolean(anchorEl);
  const id = open ? 'filter-popover' : undefined;

  const groupedChatHistory = useMemo(
    () => groupChatHistory(filteredChats),
    [filteredChats]
  );

  // Display a message if no chat history is present
  if (chatHistory.length === 0) {
    return <Typography>No chat history available.</Typography>;
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        {/* Search Bar */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            InputProps={{
              startAdornment: (
                <Search sx={{ color: 'text.secondary', mr: 1 }} />
              ),
            }}
          />
        </Box>

        <Button
          startIcon={<FilterAlt />}
          variant="outlined"
          onClick={handleClick}
          size="small"
          fullWidth
          sx={{
            borderColor: 'rgba(255, 154, 53, 0.5)',
            color: '#ff9a35',
            '&:hover': {
              borderColor: '#ff9a35',
              backgroundColor: 'rgba(255, 154, 53, 0.1)',
            },
          }}
        >
          Filter Conversations
        </Button>

        {/* Active Filters Display */}
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {filters.warm !== null && (
            <Chip
              label={filters.warm ? 'Warm' : 'Not Warm'}
              onDelete={() => setFilters((prev) => ({ ...prev, warm: null }))}
              deleteIcon={<Close />}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 154, 53, 0.1)',
                color: '#ff9a35',
                '& .MuiChip-deleteIcon': { color: '#ff9a35' },
              }}
            />
          )}
          {filters.sources.map((source) => (
            <Chip
              key={source}
              label={
                AVAILABLE_SOURCES.find((s) => s.value === source)?.label ||
                source
              }
              onDelete={() => handleSourceToggle(source)}
              deleteIcon={<Close />}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 154, 53, 0.1)',
                color: '#ff9a35',
                '& .MuiChip-deleteIcon': { color: '#ff9a35' },
              }}
            />
          ))}
          {(filters.messageLength[0] > 1 || filters.messageLength[1] < 100) && (
            <Chip
              label={`Messages: ${filters.messageLength[0]}-${
                filters.messageLength[1] === 100
                  ? '10+'
                  : filters.messageLength[1]
              }`}
              onDelete={() =>
                setFilters((prev) => ({
                  ...prev,
                  messageLength: [1, 100],
                }))
              }
              deleteIcon={<Close />}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 154, 53, 0.1)',
                color: '#ff9a35',
                '& .MuiChip-deleteIcon': { color: '#ff9a35' },
              }}
            />
          )}
        </Box>

        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, width: 300 }}>
            {/* Warm/Not Warm Filter */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.warm === true}
                  indeterminate={filters.warm === null}
                  onChange={handleWarmChange}
                />
              }
              label="Warm Leads"
            />

            {/* Sources Filter */}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Sources
            </Typography>
            <FormGroup>
              {AVAILABLE_SOURCES.map((source) => (
                <FormControlLabel
                  key={source.value}
                  control={
                    <Checkbox
                      checked={filters.sources.includes(source.value)}
                      onChange={() => handleSourceToggle(source.value)}
                    />
                  }
                  label={source.label}
                />
              ))}
            </FormGroup>

            <Divider sx={{ my: 2 }} />

            {/* Message Length Range */}
            <Typography variant="subtitle2" gutterBottom>
              Number of Messages
            </Typography>
            <Box sx={{ px: 1 }}>
              <Slider
                value={filters.messageLength}
                onChange={handleMessageLengthChange}
                valueLabelDisplay="auto"
                min={1}
                max={100}
                marks={[
                  { value: 1, label: '1' },
                  { value: 100, label: '10+' },
                ]}
                valueLabelFormat={(value) => (value === 100 ? '10+' : value)}
                sx={{
                  '& .MuiSlider-thumb': {
                    backgroundColor: '#ff9a35',
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: '#ff9a35',
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: 'rgba(255, 154, 53, 0.3)',
                  },
                  '& .MuiSlider-mark': {
                    backgroundColor: '#ff9a35',
                  },
                }}
              />
            </Box>
          </Box>
        </Popover>
      </Box>

      {(
        Object.keys(groupedChatHistory) as Array<
          keyof typeof groupedChatHistory
        >
      ).map((groupKey) => (
        <Stack key={groupKey} spacing={1}>
          {groupedChatHistory[groupKey].length > 0 && (
            <>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 'bold',
                  padding: '6px',
                  paddingTop: '16px',
                  color: '#ff9a35',
                }}
              >
                {groupKey[0].toUpperCase() +
                  groupKey.slice(1).replace(/_/g, ' ')}
              </Typography>
              {groupedChatHistory[groupKey]
                .sort((a, b) => b.timestamp - a.timestamp)
                .map((chat) => (
                  <ChatHistoryItem
                    key={chat.id}
                    chat={chat}
                    currentChatID={currentChatID ?? ''}
                    setCurrentChatID={setCurrentChatID}
                  />
                ))}
            </>
          )}
        </Stack>
      ))}
    </Box>
  );
};

export default ChatHistoryList;
